import { useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Smartphone, Laptop, Tablet, Watch, Headphones, Clock, CheckCircle, DollarSign, Phone, Mail, Calendar, FileText } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ReparationsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const RepairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const RepairCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  .repair-icon {
    width: 4rem;
    height: 4rem;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin-bottom: 1.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  .price-range {
    font-size: 1.25rem;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 1rem;
  }
  
  .services {
    list-style: none;
    
    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      color: #374151;
      
      .check-icon {
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
`;

const ProcessSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  margin-bottom: 4rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 2rem;
    text-align: center;
  }
  
  .process-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    
    .process-step {
      text-align: center;
      
      .step-number {
        width: 3rem;
        height: 3rem;
        background: linear-gradient(135deg, #3b82f6, #2563eb);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        margin: 0 auto 1rem;
      }
      
      h4 {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1a202c;
        margin-bottom: 0.5rem;
      }
      
      p {
        color: #64748b;
        line-height: 1.5;
      }
    }
  }
`;

const BookingSection = styled.div`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 1rem;
  padding: 3rem;
  color: white;
  text-align: center;
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
  }
  
  p {
    margin-bottom: 2rem;
    opacity: 0.9;
    font-size: 1.125rem;
  }
  
  .booking-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
    
    .booking-btn {
      background: white;
      color: #3b82f6;
      border: none;
      border-radius: 0.5rem;
      padding: 1rem 2rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
    }
  }
`;

const ReparationsPage = () => {
  const navigate = useNavigate();

  const repairs = [
    {
      icon: <Smartphone size={32} />,
      title: 'iPhone',
      priceRange: '25 000 - 175 000 XOF',
      services: [
        'Changement écran',
        'Remplacement batterie',
        'Réparation boutons',
        'Problèmes de charge',
        'Dégât des eaux'
      ]
    },
    {
      icon: <Tablet size={32} />,
      title: 'iPad',
      priceRange: '35 000 - 250 000 XOF',
      services: [
        'Changement écran',
        'Remplacement batterie',
        'Réparation port de charge',
        'Problèmes audio',
        'Réparation connectique'
      ]
    },
    {
      icon: <Laptop size={32} />,
      title: 'MacBook',
      priceRange: '50 000 - 400 000 XOF',
      services: [
        'Changement écran',
        'Remplacement clavier',
        'Mise à niveau RAM',
        'Problèmes de démarrage',
        'Nettoyage ventilation'
      ]
    },
    {
      icon: <Watch size={32} />,
      title: 'Apple Watch',
      priceRange: '20 000 - 100 000 XOF',
      services: [
        'Changement écran',
        'Remplacement batterie',
        'Réparation couronne',
        'Problèmes étanchéité',
        'Mise à jour logiciel'
      ]
    },
    {
      icon: <Headphones size={32} />,
      title: 'AirPods',
      priceRange: '15 000 - 75 000 XOF',
      services: [
        'Remplacement batterie',
        'Réparation audio',
        'Problèmes de connectivité',
        'Nettoyage complet',
        'Remplacement coques'
      ]
    },
    {
      icon: <Wrench size={32} />,
      title: 'Accessoires',
      priceRange: '10 000 - 50 000 XOF',
      services: [
        'Réparation chargeurs',
        'Remplacement câbles',
        'Réparation coques',
        'Problèmes adaptateurs',
        'Maintenance générale'
      ]
    }
  ];

  const processSteps = [
    { number: '1', title: 'Diagnostic gratuit', description: 'Analyse complète de votre appareil' },
    { number: '2', title: 'Devis personnalisé', description: 'Tarif transparent et sans engagement' },
    { number: '3', title: 'Réparation rapide', description: 'Intervention le jour même pour la plupart des cas' },
    { number: '4', title: 'Garantie', description: '6 mois de garantie sur toutes les réparations' }
  ];

  return (
    <ReparationsContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>Services de Réparation</h1>
              <p>Réparation professionnelle et rapide pour tous vos appareils Apple par nos techniciens certifiés</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <RepairGrid>
              {repairs.map((repair, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  <RepairCard>
                    <div className="repair-icon">
                      {repair.icon}
                    </div>
                    <h3>{repair.title}</h3>
                    <div className="price-range">{repair.priceRange}</div>
                    <ul className="services">
                      {repair.services.map((service, idx) => (
                        <li key={idx}>
                          <CheckCircle className="check-icon" size={16} />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </RepairCard>
                </motion.div>
              ))}
            </RepairGrid>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ProcessSection>
              <h2>Notre processus de réparation</h2>
              <div className="process-grid">
                {processSteps.map((step, index) => (
                  <div key={index} className="process-step">
                    <div className="step-number">{step.number}</div>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
            </ProcessSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <BookingSection>
              <h2>Prêt à réparer votre appareil ?</h2>
              <p>Réservez votre rdv de diagnostic gratuit ou contactez-nous pour un devis personnalisé</p>
              <div className="booking-buttons">
                <a href="/contact" className="booking-btn" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>
                  <Calendar size={18} />
                  Prendre RDV
                </a>
                <a href="/sav" className="booking-btn" onClick={(e) => { e.preventDefault(); navigate('/sav'); }}>
                  <Phone size={18} />
                  Contacter SAV
                </a>
                <a href="tel:+22520000000" className="booking-btn">
                  <Phone size={18} />
                  Appeler
                </a>
              </div>
            </BookingSection>
          </motion.div>
        </Container>
      </Section>
    </ReparationsContainer>
  );
};

export default ReparationsPage;
